import {cargo} from "async";
import {LanguageCode} from "../schema";

export type FileUploadData = {
  id: string | null;
  recognizedText: string | null;
  uuid: string | null;
  duration: number | null;
  isError: boolean;
};

export type FileUpload = {
  data: FileUploadData;
  chunk: (chunk: Blob) => void;
  done: () => Promise<void>;
  destroy: () => void;
  waitForConnection: () => Promise<void>;
};

export default function createFileUploadWS(
  baseEndpoint: string,
  lang: LanguageCode,
  engine: string | null,
  challengeId: string | null,
  accessKey: string | null,
  onGetResult: () => void,
  contentType?: string,
  pipeline?: string,
  sampleRate?: number,
): FileUpload {
  let isConnectionReady = false;
  let isDestroyed = false;
  let connectionReadyResolve: (() => void) | null = null;
  let connectionReadyReject: ((error: Error) => void) | null = null;
  let connectionTimeout: NodeJS.Timeout;

  const connectionReadyPromise = new Promise<void>((resolve, reject) => {
    connectionReadyResolve = resolve;
    connectionReadyReject = reject;
  });

  const ws = new WebSocket(
    `${baseEndpoint}/${contentType}/${pipeline}/${sampleRate}/${lang}/${engine}/${challengeId}/${accessKey}/`,
  );

  const file: FileUpload = {
    data: {
      id: null,
      recognizedText: null,
      uuid: null,
      duration: null,
      isError: false,
    },
    chunk: (chunk: Blob) => sendQueue.push(chunk),
    done: () => (sendQueue.idle() ? Promise.resolve() : sendQueue.drain()),
    destroy: () => {
      isDestroyed = true;
      clearTimeout(connectionTimeout);
      sendQueue.kill();
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    },
    waitForConnection: () => connectionReadyPromise,
  };

  connectionTimeout = setTimeout(() => {
    if (!isConnectionReady && !isDestroyed) {
      console.error("[FileUploadWS] Connection timeout");
      file.data.isError = true;
      if (connectionReadyReject) {
        connectionReadyReject(new Error("WebSocket connection timeout"));
      }
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    }
  }, 6000);

  ws.onopen = () => {
    clearTimeout(connectionTimeout);
    isConnectionReady = true;
    if (connectionReadyResolve) {
      connectionReadyResolve();
    }
  };

  ws.onerror = (error) => {
    console.error("[FileUploadWS] Connection error:", error);
    clearTimeout(connectionTimeout);
    file.data.isError = true;
    isConnectionReady = false;
    if (connectionReadyReject && !isDestroyed) {
      connectionReadyReject(new Error("WebSocket connection error"));
    }
  };

  ws.onclose = (event) => {
    console.warn("[FileUploadWS] Connection closed:", event.code, event.reason);
    clearTimeout(connectionTimeout);
    isConnectionReady = false;

    if (!file.data.uuid && !file.data.isError && !isDestroyed) {
      file.data.isError = true;
      onGetResult();
    }
  };

  ws.onmessage = (e: MessageEvent) => {
    if (e.data === "error") {
      file.data.id = e.data;
      file.data.isError = true;
      ws.close();
      onGetResult();
      return;
    }
    if (e.data === "uploaded") {
      ws.close();
      onGetResult();
      return;
    }
    let json = undefined;
    try {
      json = JSON.parse(e.data);
      if (json["uuid"]) {
        file.data.uuid = json["uuid"];
        file.data.recognizedText = json["text"];
        file.data.duration = json["duration"];
        ws.close();
        onGetResult();
      }
    } catch (error) {
      file.data.id = e.data;
    }
  };

  const sendQueue = cargo<Blob>((blobs, callback) => {
    (async () => {
      await connectionReadyPromise;

      if (isDestroyed) throw new Error("FileUpload was destroyed");

      await sendData(ws, new Blob(blobs));
    })()
      .then(() => callback())
      .catch((e) => {
        console.error("[FileUploadWS] Send error:", e);

        if (isConnectionReady && !isDestroyed && ws.readyState === WebSocket.OPEN) {
          setTimeout(() => {
            sendQueue.unshift(blobs);
            callback();
          }, 100);
        } else {
          file.data.isError = true;
          callback(e);
        }
      });
  });

  return file;
}

async function sendData(ws: WebSocket, body: Blob | null = null) {
  if (ws.readyState === ws.OPEN && body) {
    ws.send(body);
  } else {
    throw new Error(
      "Cannot send audio data via websocket. Connection is not established or there is no data in body.",
    );
  }
}
